import React, { useState } from 'react';

import { Link } from 'react-router-dom';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

import { TableHeaderCell } from '../../../widgets/table-components/table-header-cell';

type ColumnConfig<T extends object> = {
  key: keyof T;
  label: string;
  linkGenerator?: (row: T) => string | { link: string; value: string }[];
  absoluteLinkGenerator?: (row: T) => string;
  valueDisplayGenerator?: (row: T) => string;
};

type InputProps<T extends object> = {
  rows: T[];
  rowIdKey: keyof T;
  columns: ColumnConfig<T>[];
};

const ROWS_PER_PAGE = 20;

export const TwilioRegistrationInfoTable = <T extends object>({
  rows,
  rowIdKey,
  columns,
}: InputProps<T>) => {
  const [offset, setOffset] = useState(0);

  const handleFullChangePage = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number,
  ) => {
    setOffset(page * ROWS_PER_PAGE);
  };

  const paginatedRows = rows.slice(offset, offset + ROWS_PER_PAGE);

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableHeaderCell key={c.label}>{c.label}</TableHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedRows.map((row) => (
            <TableRow key={String(row[rowIdKey])}>
              {columns.map((c) => {
                const value = row[c.key];
                const key = `column-${String(c.key)}-${String(JSON.stringify(value))}`;
                if (c.linkGenerator) {
                  const link = c.linkGenerator(row);
                  if (Array.isArray(link)) {
                    return (
                      <TableCell key={key}>
                        {link.map((l) => (
                          <div key={`${l.value}-${l.link}`}>
                            <Link className="typo-link" target="_blank" to={l.link}>
                              {l.value}
                            </Link>
                          </div>
                        ))}
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={key}>
                      <Link className="typo-link" target="_blank" to={link}>
                        {value}
                      </Link>
                    </TableCell>
                  );
                }
                if (c.absoluteLinkGenerator) {
                  const link = c.absoluteLinkGenerator(row);
                  return (
                    <TableCell key={key}>
                      <a
                        className="typo-link"
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {value}
                      </a>
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={key}>
                    {c.valueDisplayGenerator ? c.valueDisplayGenerator(row) : value}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={rows.length}
        rowsPerPage={ROWS_PER_PAGE}
        page={offset / ROWS_PER_PAGE}
        backIconButtonProps={{
          'aria-label': 'Previous Page',
          size: 'small',
        }}
        nextIconButtonProps={{
          'aria-label': 'Next Page',
          size: 'small',
        }}
        onPageChange={handleFullChangePage}
        rowsPerPageOptions={[ROWS_PER_PAGE]}
      />
    </>
  );
};
